/**
 * Modal Abonnement Individuel
 * ============================
 * 
 * RÈGLES MOA P1 (23 Jan 2026):
 * - Responsive mobile (scroll interne, CTA sticky)
 * - Prix depuis API (source de vérité: Stripe)
 * - Routage intelligent: nouvelle souscription OU upgrade/downgrade
 * - Aucun message bloquant - chaque pack est cliquable
 */

import React, { useState, useEffect } from 'react';
import { X, Gift, Star, Crown, Loader2, AlertTriangle, Check, Sparkles, RefreshCw } from 'lucide-react';
import { getBackendUrl } from '../../config/environment';
import { toast } from 'sonner';

const BACKEND_URL = getBackendUrl();

// Icons par pack
const PACK_ICONS = {
  'pack_decouverte': Gift,
  'pack_standard': Star,
  'pack_pro': Crown
};

// Couleurs par pack — palette premium ivoire/or/violet
const PACK_COLORS = {
  'pack_decouverte': {
    gradient: 'from-emerald-500 to-teal-600',
    ringActive: '#48825A',
    accent: '#48825A',
    accentSoft: '#E8F1EA',
  },
  'pack_standard': {
    gradient: 'from-amber-500 to-orange-600',
    ringActive: '#D4AF37',
    accent: '#B7901F',
    accentSoft: '#FFFCEC',
  },
  'pack_pro': {
    gradient: 'from-purple-500 to-indigo-600',
    ringActive: '#7A4FCF',
    accent: '#5B3CA3',
    accentSoft: '#F4EFFF',
  }
};

// Palette design system
const T = {
  bg: '#FBF9F3',
  card: '#FFFFFF',
  ink: '#1C1147',
  inkSoft: '#3F356C',
  text: '#1F2937',
  muted: '#6B7280',
  mutedSoft: '#9CA3AF',
  border: '#E8E2D2',
  gold: '#D4AF37',
  goldDeep: '#B7901F',
  goldLight: '#F3D060',
  ivoryDeep: '#FFFCEC',
  green: '#48825A',
  red: '#9B2C1F',
};

export function IndividualSubscriptionModal({ isOpen, onClose }) {
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState('pack_standard');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPacks, setIsLoadingPacks] = useState(true);
  const [error, setError] = useState(null);
  
  // 🎯 P1 MOA: Détection abonnement existant pour routage intelligent
  const [hasSubscription, setHasSubscription] = useState(false);
  const [currentPackId, setCurrentPackId] = useState(null);

  // Charger les packs ET le statut d'abonnement
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchData = async () => {
      setIsLoadingPacks(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        // 🎯 P1 MOA: Fetch packs ET statut abonnement en parallèle
        const [packsRes, subscriptionRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/billing/individual-packs`, { headers }),
          fetch(`${BACKEND_URL}/api/billing/my-subscription`, { headers }).catch(() => null)
        ]);
        
        // Gérer erreurs packs
        if (packsRes.status === 403) {
          setError('b2b_blocked');
          return;
        }
        
        if (packsRes.status === 503) {
          setError('stripe_unavailable');
          return;
        }
        
        if (!packsRes.ok) {
          throw new Error('Erreur chargement packs');
        }
        
        const packsData = await packsRes.json();
        setPacks(packsData);
        
        // 🎯 P1 MOA: Détecter si utilisateur a un abonnement actif
        if (subscriptionRes && subscriptionRes.ok) {
          const subData = await subscriptionRes.json();
          setHasSubscription(subData.has_subscription || false);
          setCurrentPackId(subData.pack_id || null);
          
          // Si abonné, présélectionner le pack populaire différent du pack actuel
          if (subData.has_subscription && subData.pack_id) {
            const popularPack = packsData.find(p => p.is_popular && p.id !== subData.pack_id);
            if (popularPack) {
              setSelectedPack(popularPack.id);
            } else {
              // Sinon, sélectionner le premier pack différent
              const differentPack = packsData.find(p => p.id !== subData.pack_id);
              if (differentPack) {
                setSelectedPack(differentPack.id);
              }
            }
          } else {
            // Non abonné: présélectionner le pack populaire
            const popularPack = packsData.find(p => p.is_popular);
            if (popularPack) {
              setSelectedPack(popularPack.id);
            }
          }
        } else {
          // Pas d'info subscription = nouveau client
          setHasSubscription(false);
          setCurrentPackId(null);
          const popularPack = packsData.find(p => p.is_popular);
          if (popularPack) {
            setSelectedPack(popularPack.id);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('load_error');
      } finally {
        setIsLoadingPacks(false);
      }
    };
    
    fetchData();
  }, [isOpen]);

  // 🎯 P1 MOA: Routage intelligent - nouvelle souscription OU upgrade/downgrade
  const handleSubscribe = async () => {
    if (!selectedPack) {
      toast.error('Veuillez sélectionner une offre');
      return;
    }
    
    // Empêcher de sélectionner le même pack
    if (hasSubscription && selectedPack === currentPackId) {
      toast.info('Vous êtes déjà sur ce pack');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // 🎯 P1 MOA: Router vers le bon endpoint selon le statut d'abonnement
      const endpoint = hasSubscription 
        ? `${BACKEND_URL}/api/billing/create-upgrade-checkout`
        : `${BACKEND_URL}/api/billing/create-individual-subscription`;
      
      const bodyPayload = hasSubscription
        ? { new_pack_id: selectedPack, origin_url: window.location.origin }
        : { pack_id: selectedPack, origin_url: window.location.origin };
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bodyPayload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors du traitement');
      }
      
      const data = await response.json();
      
      // Les deux endpoints retournent une URL de redirection
      const redirectUrl = data.url || data.checkout_url;
      if (redirectUrl) {
        toast.success('Redirection vers le paiement...');
        window.location.href = redirectUrl;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (err) {
      console.error('Error processing subscription:', err);
      toast.error(err.message || 'Erreur lors du traitement');
    } finally {
      setIsLoading(false);
    }
  };

  // Formater le prix avec la devise
  const formatPrice = (price, currency) => {
    const formatted = price.toFixed(2);
    if (currency === 'eur') return `${formatted}€`;
    if (currency === 'usd') return `$${formatted}`;
    return `${formatted} ${currency.toUpperCase()}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[10003] flex items-center justify-center p-2 sm:p-4 overscroll-contain"
      style={{
        background: 'rgba(28,17,71,0.55)',
        backdropFilter: 'blur(10px)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Modal Container — fond ivoire lumineux */}
      <div
        className="relative w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] rounded-3xl flex flex-col overflow-hidden"
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow:
            '0 30px 80px -20px rgba(28,17,71,0.5), 0 0 0 1px rgba(212,175,55,0.15)',
        }}
        data-testid="individual-subscription-modal"
      >
        {/* Halo or top */}
        <div
          className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(243,208,96,0.35) 0%, transparent 70%)',
          }}
        />

        {/* Header — sticky */}
        <div
          className="flex-shrink-0 relative p-4 sm:p-6"
          style={{ borderBottom: `1px solid ${T.border}` }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2.5 rounded-full transition-colors z-[10004] touch-manipulation hover:bg-black/5"
            data-testid="close-modal-btn"
            aria-label="Fermer la modale"
            style={{ color: T.muted }}
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center pr-10 relative">
            <h2
              className="text-xl sm:text-2xl mb-1 sm:mb-2"
              style={{
                color: T.ink,
                fontFamily: 'Georgia, serif',
                fontWeight: 600,
              }}
            >
              {hasSubscription ? 'Changer de pack' : 'Choisissez votre abonnement'}
            </h2>
            <p className="text-sm sm:text-base" style={{ color: T.muted }}>
              {hasSubscription
                ? 'Upgrade ou downgrade instantané'
                : 'Accès complet à COACHDIA'}
            </p>
          </div>
        </div>

        {/* Content — Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 relative">
          {/* Loading */}
          {isLoadingPacks && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: T.goldDeep }} />
            </div>
          )}

          {/* Error B2B */}
          {error === 'b2b_blocked' && (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: T.goldDeep }} />
              <h3 className="text-lg mb-2" style={{ color: T.ink, fontWeight: 600 }}>
                Accès Entreprise détecté
              </h3>
              <p style={{ color: T.muted }}>
                Votre compte est géré par votre entreprise. Contactez votre administrateur.
              </p>
            </div>
          )}

          {error === 'stripe_unavailable' && (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: '#D97706' }} />
              <h3 className="text-lg mb-2" style={{ color: T.ink, fontWeight: 600 }}>
                Service temporairement indisponible
              </h3>
              <p className="mb-4" style={{ color: T.muted }}>
                Impossible de charger les tarifs. Veuillez réessayer dans quelques instants.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2 rounded-full font-semibold text-sm"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                  color: T.ink,
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          {error === 'load_error' && (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: T.red }} />
              <h3 className="text-lg mb-2" style={{ color: T.ink, fontWeight: 600 }}>
                Erreur de chargement
              </h3>
              <p className="mb-4" style={{ color: T.muted }}>
                Impossible de charger les offres. Veuillez réessayer.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2 rounded-full font-semibold text-sm"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                  color: T.ink,
                }}
              >
                Réessayer
              </button>
            </div>
          )}

          {/* Packs Grid */}
          {!isLoadingPacks && !error && packs.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                {packs.map((pack) => {
                  const Icon = PACK_ICONS[pack.id] || Star;
                  const colors = PACK_COLORS[pack.id] || PACK_COLORS['pack_standard'];
                  const isSelected = selectedPack === pack.id;
                  const isCurrentPack = hasSubscription && currentPackId === pack.id;

                  return (
                    <div
                      key={pack.id}
                      onClick={() => !isCurrentPack && setSelectedPack(pack.id)}
                      className="relative p-4 sm:p-5 rounded-2xl transition-all duration-300 touch-manipulation"
                      style={
                        isCurrentPack
                          ? {
                              background: T.ivoryDeep,
                              border: `1.5px solid ${T.border}`,
                              opacity: 0.7,
                              cursor: 'not-allowed',
                            }
                          : isSelected
                          ? {
                              background: `linear-gradient(135deg, ${T.ivoryDeep} 0%, ${T.card} 50%, ${T.ivoryDeep} 100%)`,
                              border: `2px solid ${colors.ringActive}`,
                              boxShadow: `0 14px 34px -14px ${colors.ringActive}80, inset 0 1px 0 rgba(255,255,255,0.8)`,
                              cursor: 'pointer',
                              transform: 'translateY(-2px)',
                            }
                          : {
                              background: T.card,
                              border: `1.5px solid ${T.border}`,
                              boxShadow: '0 2px 6px -2px rgba(28,17,71,0.06)',
                              cursor: 'pointer',
                            }
                      }
                      data-testid={`pack-card-${pack.id}`}
                    >
                      {/* Current Pack Badge */}
                      {isCurrentPack && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                          <span
                            className="px-3 py-1 text-[10px] sm:text-xs font-bold rounded-full whitespace-nowrap uppercase tracking-wider"
                            style={{
                              background: T.ink,
                              color: T.goldLight,
                              boxShadow: `0 4px 10px -4px rgba(28,17,71,0.5)`,
                            }}
                          >
                            Pack actuel
                          </span>
                        </div>
                      )}

                      {/* Popular Badge */}
                      {pack.is_popular && !isCurrentPack && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10">
                          <span
                            className="px-3 py-1 text-[10px] sm:text-xs font-bold rounded-full whitespace-nowrap uppercase tracking-wider"
                            style={{
                              background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                              color: T.ink,
                              boxShadow: `0 4px 10px -4px ${T.gold}80`,
                            }}
                          >
                            Populaire
                          </span>
                        </div>
                      )}

                      {/* Icon + Name */}
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center flex-shrink-0`}
                          style={{
                            boxShadow: `0 8px 20px -8px ${colors.accent}80`,
                          }}
                        >
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div>
                          <h3
                            className="text-base sm:text-lg"
                            style={{
                              color: T.ink,
                              fontFamily: 'Georgia, serif',
                              fontWeight: 600,
                            }}
                          >
                            {pack.name}
                          </h3>
                          <p
                            className="font-medium text-xs sm:text-sm"
                            style={{ color: colors.accent }}
                          >
                            {pack.sessions_per_month} sessions/mois
                          </p>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="mb-1">
                        <span
                          className="text-2xl sm:text-3xl"
                          style={{
                            color: T.ink,
                            fontFamily: 'Georgia, serif',
                            fontWeight: 700,
                          }}
                        >
                          {formatPrice(pack.price, pack.currency)}
                        </span>
                        <span className="text-sm" style={{ color: T.muted }}>
                          /mois
                        </span>
                      </div>

                      <p className="text-xs" style={{ color: T.mutedSoft }}>
                        {formatPrice(pack.price_per_session, pack.currency)} / session
                      </p>

                      {/* Selection indicator */}
                      {isSelected && !isCurrentPack && (
                        <div className="absolute bottom-3 right-3">
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center"
                            style={{
                              background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                              boxShadow: `0 4px 10px -3px ${T.gold}aa`,
                            }}
                          >
                            <Check className="w-3.5 h-3.5" style={{ color: T.ink }} strokeWidth={3} />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Features */}
              <div
                className="mt-5 sm:mt-6 p-4 rounded-2xl"
                style={{
                  background: T.card,
                  border: `1px solid ${T.border}`,
                }}
              >
                <h4
                  className="text-xs sm:text-sm mb-3 uppercase tracking-[0.14em]"
                  style={{ color: T.goldDeep, fontWeight: 700 }}
                >
                  Inclus dans tous les abonnements
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                  {[
                    'Coach IA vocal et écrit',
                    'Modules de formation',
                    'Scénarios immersifs',
                    'Annulation à tout moment',
                  ].map((feat) => (
                    <div
                      key={feat}
                      className="flex items-center gap-2"
                      style={{ color: T.text }}
                    >
                      <Check
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0"
                        style={{ color: T.green }}
                        strokeWidth={3}
                      />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer — Sticky CTA */}
        {!isLoadingPacks && !error && packs.length > 0 && (
          <div
            className="flex-shrink-0 p-4 sm:p-6"
            style={{
              borderTop: `1px solid ${T.border}`,
              background: T.bg,
            }}
          >
            <button
              onClick={handleSubscribe}
              disabled={
                isLoading ||
                !selectedPack ||
                (hasSubscription && selectedPack === currentPackId)
              }
              className="w-full py-3.5 sm:py-4 rounded-full font-bold transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base touch-manipulation active:scale-[0.99] hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                color: T.ink,
                boxShadow: `0 12px 30px -10px ${T.gold}aa`,
              }}
              data-testid="subscribe-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  Traitement...
                </>
              ) : hasSubscription ? (
                <>
                  <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                  Changer de pack
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                  S&apos;abonner maintenant
                </>
              )}
            </button>

            <p
              className="text-center text-[10px] sm:text-xs mt-2 sm:mt-3"
              style={{ color: T.muted }}
            >
              Paiement sécurisé par Stripe · Annulation à tout moment
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default IndividualSubscriptionModal;
